import type { AvailabilityData } from "./api";
import { parseBookingTime } from "./booking-time";
import { parseBookingWallClock } from "@workspace/booking-rules";

export type TimedAvailabilityInterval = NonNullable<AvailabilityData["timedIntervals"]>[number];

/** Intervals are half-open: touching an existing interval at either boundary
 * is allowed, while any positive-width intersection is blocked. */
export function overlapsTimedAvailability(
  start: number,
  end: number,
  intervals: readonly TimedAvailabilityInterval[] | undefined,
): boolean {
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return false;
  return (intervals ?? []).some(
    interval =>
      (interval.reason === "booking" || interval.reason === "hold")
      && Number.isFinite(interval.start)
      && Number.isFinite(interval.end)
      && start < interval.end
      && end > interval.start,
  );
}

export function parseBookingDateTime(date: string, time: string): number | null {
  if (!date || !time) return null;
  const parsedTime = parseBookingTime(time);
  if (!parsedTime) return null;
  // Booking timestamps use the project's wall-clock-UTC convention. Parsing
  // as browser-local time would shift the selected interval for renters whose
  // device timezone differs from the car/event timezone.
  return parseBookingWallClock(`${date}T${String(parsedTime.hour24).padStart(2, "0")}:${String(parsedTime.minute).padStart(2, "0")}:00Z`)?.date.getTime() ?? null;
}

export function hasTimedBookingConflict(
  dateFrom: string,
  dateTo: string,
  pickupTime: string,
  dropoffTime: string,
  intervals: readonly TimedAvailabilityInterval[] | undefined,
): boolean {
  const start = parseBookingDateTime(dateFrom, pickupTime);
  const end = parseBookingDateTime(dateTo, dropoffTime);
  return start != null && end != null && overlapsTimedAvailability(start, end, intervals);
}